/* ==========================================================================
   VAN LYNN — theme.js
   Core utilities and shared custom elements. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  const VL = (window.VL = window.VL || {});

  /* ---------- Utilities ---------- */
  VL.debounce = function (fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  VL.fetchConfig = function (type = 'json', body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}`, 'X-Requested-With': 'XMLHttpRequest' },
      body: body ? JSON.stringify(body) : undefined,
    };
  };

  VL.parseHTML = function (html) {
    return new DOMParser().parseFromString(html, 'text/html');
  };

  VL.announce = function (message) {
    const region = document.getElementById('A11yAnnouncer');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => (region.textContent = message), 60);
  };

  VL.toast = function (message, type = 'default', timeout = 3600) {
    const region = document.querySelector('[data-toast-region]');
    if (!region || !message) return;
    const el = document.createElement('div');
    el.className = `toast${type === 'error' ? ' toast--error' : ''}`;
    el.setAttribute('role', type === 'error' ? 'alert' : 'status');
    el.textContent = message;
    region.appendChild(el);
    window.setTimeout(() => {
      el.classList.add('is-leaving');
      el.addEventListener('animationend', () => el.remove(), { once: true });
      window.setTimeout(() => el.remove(), 600);
    }, timeout);
  };

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), summary';

  VL.getFocusable = function (container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  };

  let trapHandler = null;
  VL.trapFocus = function (container, elementToFocus) {
    VL.removeTrapFocus();
    trapHandler = (event) => {
      if (event.key !== 'Tab') return;
      const els = VL.getFocusable(container);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler);
    const target = elementToFocus || VL.getFocusable(container)[0] || container;
    window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
  };
  VL.removeTrapFocus = function (elementToFocus) {
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
    if (elementToFocus && document.contains(elementToFocus)) elementToFocus.focus({ preventScroll: true });
  };

  let lockCount = 0;
  VL.lockScroll = function () {
    lockCount += 1;
    document.body.classList.add('has-overlay');
  };
  VL.unlockScroll = function () {
    lockCount = Math.max(0, lockCount - 1);
    if (!lockCount) document.body.classList.remove('has-overlay');
  };

  /* ---------- Overlay (drawer / modal) element ---------- */
  class OverlayPanel extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-overlay-panel]') || this.firstElementChild;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-overlay-close]')) {
          e.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.close();
        }
      });
    }
    get isOpen() {
      return this.classList.contains('is-open');
    }
    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      VL.lockScroll();
      const focusTarget = this.querySelector('[data-autofocus]');
      VL.trapFocus(this.panel || this, focusTarget);
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }
    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'false');
      VL.unlockScroll();
      VL.removeTrapFocus(this.trigger);
      window.setTimeout(() => {
        if (!this.isOpen) this.setAttribute('inert', '');
      }, 600);
      this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }
  }
  if (!customElements.get('overlay-panel')) customElements.define('overlay-panel', OverlayPanel);

  VL.openOverlay = function (id, trigger) {
    const el = document.getElementById(id);
    if (el && typeof el.open === 'function') el.open(trigger);
    return el;
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-overlay]');
    if (!opener) return;
    const id = opener.getAttribute('data-open-overlay');
    if (id === 'CartDrawer' && window.theme.settings.cartType !== 'drawer') return;
    const el = document.getElementById(id);
    if (!el) return;
    event.preventDefault();
    el.open(opener);
  });

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'none';
      this.transparent = this.hasAttribute('data-transparent');
      this.section = this.closest('.shopify-section') || this;
      this.lastY = window.scrollY;
      this.ticking = false;
      if (this.mode !== 'none') this.section.classList.add('header-section--sticky');
      if (this.transparent) document.body.classList.add('has-transparent-header');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
      this.setHeightVar();
      this.resizeObserver = new ResizeObserver(() => this.setHeightVar());
      this.resizeObserver.observe(this);
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      document.body.classList.remove('has-transparent-header');
    }
    setHeightVar() {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    }
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }
    update() {
      const y = window.scrollY;
      const scrolled = y > 40;
      this.classList.toggle('is-scrolled', scrolled);
      if (this.transparent) this.classList.toggle('is-transparent', !scrolled && !this.querySelector('.is-menu-open'));
      if (this.mode === 'scroll-up') {
        const hide = y > this.lastY && y > this.offsetHeight * 2 && !this.contains(document.activeElement);
        this.section.classList.toggle('header-section--hidden', hide);
      }
      this.lastY = y;
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ---------- Desktop dropdown / mega menu ---------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-menu-item]'));
      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-menu-toggle]');
        const panel = item.querySelector('[data-menu-panel]');
        if (!toggle || !panel) return;
        let timer;
        const open = () => {
          clearTimeout(timer);
          this.closeAll(item);
          item.classList.add('is-menu-open');
          toggle.setAttribute('aria-expanded', 'true');
          this.closest('sticky-header')?.classList.remove('is-transparent');
        };
        const close = () => {
          item.classList.remove('is-menu-open');
          toggle.setAttribute('aria-expanded', 'false');
          this.closest('sticky-header')?.update?.();
        };
        item._close = close;
        if (window.matchMedia('(hover: hover)').matches) {
          item.addEventListener('mouseenter', open);
          item.addEventListener('mouseleave', () => (timer = setTimeout(close, 160)));
        }
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          item.classList.contains('is-menu-open') ? close() : open();
        });
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && item.classList.contains('is-menu-open')) {
            close();
            toggle.focus();
          }
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) close();
        });
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closeAll();
      });
    }
    closeAll(except) {
      this.items.forEach((i) => i !== except && i._close && i._close());
    }
  }
  if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);

  /* ---------- Smooth accordion (details/summary) ---------- */
  class SmoothDetails {
    constructor(details) {
      this.el = details;
      this.summary = details.querySelector('summary');
      this.content = details.querySelector('.accordion__content');
      if (!this.summary || !this.content) return;
      this.summary.addEventListener('click', (e) => this.onClick(e));
    }
    onClick(e) {
      if (!document.documentElement.classList.contains('motion-ok')) return;
      e.preventDefault();
      if (this.anim) this.anim.cancel();
      const startH = `${this.el.offsetHeight}px`;
      if (!this.el.open) {
        this.el.open = true;
        const endH = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
        this.animate(startH, endH, true);
      } else {
        const endH = `${this.summary.offsetHeight}px`;
        this.animate(startH, endH, false);
      }
    }
    animate(from, to, opening) {
      this.el.style.overflow = 'hidden';
      this.anim = this.el.animate({ height: [from, to] }, { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      this.anim.onfinish = () => {
        this.el.open = opening;
        this.el.style.overflow = '';
        this.anim = null;
      };
    }
  }
  VL.initAccordions = function (root = document) {
    root.querySelectorAll('details.accordion:not([data-smooth])').forEach((d) => {
      d.setAttribute('data-smooth', '');
      new SmoothDetails(d);
    });
  };

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const prev = this.input.value;
          btn.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
          if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.updateButtons();
        })
      );
      this.input.addEventListener('change', () => this.updateButtons());
      this.updateButtons();
    }
    updateButtons() {
      const v = parseInt(this.input.value, 10);
      const min = parseInt(this.input.min || '0', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus) minus.disabled = v <= min;
      if (plus) plus.disabled = max !== null && v >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Generic scroll slider with prev/next ---------- */
  class ScrollSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.counter = this.querySelector('[data-slider-counter]');
      if (!this.track) return;
      const step = () => {
        const item = this.track.firstElementChild;
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth;
      };
      const behavior = () => (document.documentElement.classList.contains('motion-ok') ? 'smooth' : 'auto');
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: behavior() }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: behavior() }));
      this.track.addEventListener('scroll', VL.debounce(() => this.update(), 60), { passive: true });
      this.update();
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      if (this.counter && this.track.children.length) {
        const w = this.track.firstElementChild.getBoundingClientRect().width || 1;
        const idx = Math.round(this.track.scrollLeft / w) + 1;
        this.counter.textContent = `${Math.min(idx, this.track.children.length)} / ${this.track.children.length}`;
      }
    }
  }
  if (!customElements.get('scroll-slider')) customElements.define('scroll-slider', ScrollSlider);

  /* ---------- Share button ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    e.preventDefault();
    const url = btn.dataset.shareUrl || window.location.href;
    const title = btn.dataset.shareTitle || document.title;
    if (navigator.share) {
      try {
        await navigator.share({ url, title });
      } catch (err) {
        /* user cancelled */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      VL.toast(window.theme.strings.copied);
    } catch (err) {
      window.prompt('', url);
    }
  });

  /* ---------- Recently viewed (browser storage only) ---------- */
  const RV_KEY = 'vl:recently-viewed';
  VL.recentlyViewed = {
    get() {
      try {
        return JSON.parse(window.localStorage.getItem(RV_KEY) || '[]');
      } catch (e) {
        return [];
      }
    },
    add(handle) {
      if (!handle) return;
      try {
        const list = this.get().filter((h) => h !== handle);
        list.unshift(handle);
        window.localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
      } catch (e) {
        /* storage unavailable */
      }
    },
  };

  /**
   * Renders product cards for a list of handles using the Section Rendering API
   * (section "product-card-fragment" rendered in the context of each product URL).
   */
  VL.renderProductCards = async function (handles, container) {
    const root = window.theme.routes.root.replace(/\/$/, '');
    const results = await Promise.all(
      handles.map((h) =>
        fetch(`${root}/products/${encodeURIComponent(h)}?section_id=product-card-fragment`)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      )
    );
    container.innerHTML = '';
    let count = 0;
    results.forEach((html) => {
      if (!html) return;
      const card = VL.parseHTML(html).querySelector('[data-card-fragment] > *');
      if (card) {
        container.appendChild(card);
        count += 1;
      }
    });
    document.dispatchEvent(new CustomEvent('vl:content-loaded', { detail: { root: container } }));
    return count;
  };

  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const exclude = this.dataset.exclude;
      const limit = parseInt(this.dataset.limit || '4', 10);
      const handles = VL.recentlyViewed.get().filter((h) => h !== exclude).slice(0, limit);
      const grid = this.querySelector('[data-recent-grid]');
      if (!handles.length || !grid) return;
      const count = await VL.renderProductCards(handles, grid);
      if (count) this.hidden = false;
    }
  }
  if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);

  /* ---------- Product recommendations (Shopify API) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = () => {
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((text) => {
            const html = VL.parseHTML(text);
            const fresh = html.querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim().length) {
              this.innerHTML = fresh.innerHTML;
              this.closest('[data-recommendations-wrapper]')?.removeAttribute('hidden');
              document.dispatchEvent(new CustomEvent('vl:content-loaded', { detail: { root: this } }));
            }
          })
          .catch(() => {});
      };
      if (!('IntersectionObserver' in window)) return load();
      new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Smooth scroll for in-page anchors (opt-in) ---------- */
  if (document.body && document.body.hasAttribute('data-smooth-scroll')) {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href').length < 2) return;
      const target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target || !document.documentElement.classList.contains('motion-ok')) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }

  /* ---------- Contact / generic form: client-side validation messages ---------- */
  VL.initFormValidation = function (root = document) {
    root.querySelectorAll('form[data-validate]:not([data-validate-ready])').forEach((form) => {
      form.setAttribute('data-validate-ready', '');
      form.setAttribute('novalidate', '');
      form.addEventListener('submit', (e) => {
        let firstInvalid = null;
        form.querySelectorAll('input, select, textarea').forEach((field) => {
          const errorEl = form.querySelector(`[data-error-for="${field.id}"]`);
          if (!field.checkValidity()) {
            firstInvalid = firstInvalid || field;
            field.setAttribute('aria-invalid', 'true');
            if (errorEl) {
              errorEl.textContent = field.validationMessage;
              errorEl.hidden = false;
              field.setAttribute('aria-describedby', errorEl.id);
            }
          } else {
            field.removeAttribute('aria-invalid');
            if (errorEl) errorEl.hidden = true;
          }
        });
        if (firstInvalid) {
          e.preventDefault();
          firstInvalid.focus();
        }
      });
    });
  };

  /* ---------- Init ---------- */
  const init = (root = document) => {
    VL.initAccordions(root);
    VL.initFormValidation(root);
  };
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('vl:content-loaded', (e) => init(e.detail?.root || document));
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
